import { Routes } from '@angular/router';
import { ACTIVE_TENANT } from './core/tenant/tenants';
import { accountsEnabledGuard, unifiedAccountGuard } from './core/guards/unified-account.guard';
import { RouteSeoData } from './core/seo/seo.config';
import { DESTINATIONS, DESTINATION_PATH_PREFIX } from './features/destinations/destinations.index';

const NOINDEX: { seo: RouteSeoData } = { seo: { robots: 'noindex, follow' } };
const NOINDEX_NOFOLLOW: { seo: RouteSeoData } = { seo: { robots: 'noindex, nofollow' } };

// One prerendered SEO landing page per destination, e.g. /hotels-in-mombasa
const destinationRoutes: Routes = DESTINATIONS.map(destination => ({
  path: `${DESTINATION_PATH_PREFIX}${destination.slug}`,
  loadComponent: () =>
    import('./features/destinations/destination-page/destination-page.component').then(
      m => m.DestinationPageComponent
    ),
  data: { destinationSlug: destination.slug }
}));

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
    children: [
      {
        path: '',
        redirectTo: '/home',
        pathMatch: 'full'
      },
      {
        path: 'home',
        loadChildren: () => import('./features/home/home.routes').then(m => m.HOME_ROUTES),
        data: {
          preload: true,
          priority: 'critical'
        }
      },
      // The app is served under the /hotels/ base-href, so the hotels feature
      // routes live at the app root (e.g. /hotels/list) rather than nesting under
      // a second 'hotels' segment (which produced /hotels/hotels/list).
      {
        path: 'list',
        loadComponent: () => import('./features/hotels/hotel-list/hotel-list.component').then(m => m.HotelListComponent),
        title: `Hotel Results - ${ACTIVE_TENANT.productName}`,
        data: {
          ...NOINDEX,
          preload: true,
          priority: 'high', // High priority - users likely to navigate here first
          allowMiniApp: true,
          // Keep the SRP component alive across the funnel (list -> detail -> back).
          reuseComponent: true
        }
      },
      {
        path: 'search',
        loadComponent: () => import('./features/hotels/hotel-search/hotel-search.component').then(m => m.HotelSearchComponent),
        title: `Search Hotels - ${ACTIVE_TENANT.productName}`,
        data: { ...NOINDEX, allowMiniApp: true }
      },
      {
        path: 'detail/:id',
        loadComponent: () => import('./features/hotels/hotel-detail/hotel-detail.component').then(m => m.HotelDetailComponent),
        data: { allowMiniApp: true }
      },
      ...destinationRoutes,
      // Backward-compatible redirects from the previous nested /hotels/* paths.
      { path: 'hotels/list', redirectTo: '/list', pathMatch: 'full' },
      { path: 'hotels/search', redirectTo: '/search', pathMatch: 'full' },
      { path: 'hotels/detail/:id', redirectTo: '/detail/:id', pathMatch: 'full' },
      { path: 'hotels', redirectTo: '/list', pathMatch: 'full' },
      {
        path: 'booking',
        loadChildren: () => import('./features/booking/booking.routes').then(m => m.BOOKING_ROUTES),
        data: { 
          ...NOINDEX_NOFOLLOW,
          preload: true,
          priority: 'normal'
        }
      },
      {
        path: 'profile',
        loadChildren: () => import('./features/profile/profile.routes').then(m => m.PROFILE_ROUTES),
        canMatch: [accountsEnabledGuard],
        canActivate: [unifiedAccountGuard],
        data: { 
          ...NOINDEX_NOFOLLOW,
          preload: true,
          priority: 'low' // Low priority - less frequently accessed
        }
      },
      {
        path: 'auth',
        loadChildren: () => import('./features/auth/auth.routes').then(m => m.AUTH_ROUTES),
        canMatch: [accountsEnabledGuard],
        data: { 
          ...NOINDEX_NOFOLLOW,
          preload: false // Don't preload auth routes - only load when needed
        }
      }
    ]
  },
  {
    path: '**',
    loadComponent: () => import('./shared/components/not-found/not-found.component').then(m => m.NotFoundComponent),
    data: NOINDEX
  }
];
